import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Activity } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/scorecard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex">
      {/* Left Panel - Branding (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <img 
          src="https://customer-assets.emergentagent.com/job_b5f4e08a-0f00-46b6-800d-0a308fa6e163/artifacts/srflyfbz_PH%20Tennis%20Top%20View.png"
          alt="Tennis player"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/40 to-[#0F172A]/70"></div>
        
        {/* Content */}
        <div className="relative z-10 p-8 xl:p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_metrics-hub-117/artifacts/yhkjnbgq_63ed5a01637c0a0b27bc74fc_Players-Health_logo-2.svg" 
              alt="Players Health" 
              className="h-10 w-auto brightness-0 invert"
            />
            <div className="flex items-center gap-1.5">
              <div className="w-px h-6 bg-white/30"></div>
              <div className="flex items-center gap-1">
                <Activity className="w-5 h-5 text-[#2563EB]" strokeWidth={2} />
                <span className="text-white font-semibold text-lg tracking-tight">Pulse</span>
              </div>
            </div>
          </div>
          
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-white tracking-tight mb-4 drop-shadow-lg">
              Trust is Built Here
            </h2>
            <p className="text-white/90 text-base xl:text-lg leading-relaxed drop-shadow-md">
              Pulse is built to earn trust and operate in truth.
            </p>
            <p className="text-white/90 text-base xl:text-lg leading-relaxed mt-3 drop-shadow-md">
              Every metric reflects reality—no assumptions, no inflated numbers, no shortcuts.
            </p>
            <p className="text-white/90 text-base xl:text-lg leading-relaxed mt-3 drop-shadow-md">
              This platform exists to ensure our decisions, our accountability, and our commitments are rooted in what is true.
            </p>
          </div>

          <div className="text-white/60 text-sm drop-shadow">
            © {new Date().getFullYear()} Players Health. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_metrics-hub-117/artifacts/yhkjnbgq_63ed5a01637c0a0b27bc74fc_Players-Health_logo-2.svg" 
              alt="Players Health" 
              className="h-8 w-auto"
            />
            <div className="flex items-center gap-1.5">
              <div className="w-px h-5 bg-[#E5E7EB]"></div>
              <div className="flex items-center gap-1">
                <Activity className="w-4 h-4 text-[#2563EB]" strokeWidth={2} />
                <span className="text-[#111827] font-semibold text-sm tracking-tight">Pulse</span>
              </div>
            </div>
          </div>

          {/* Desktop Logo - Centered above form */}
          <div className="hidden lg:flex items-center justify-center gap-2 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_metrics-hub-117/artifacts/yhkjnbgq_63ed5a01637c0a0b27bc74fc_Players-Health_logo-2.svg" 
              alt="Players Health" 
              className="h-10 w-auto"
            />
            <div className="flex items-center gap-1.5">
              <div className="w-px h-6 bg-[#E5E7EB]"></div>
              <div className="flex items-center gap-1">
                <Activity className="w-5 h-5 text-[#2563EB]" strokeWidth={2} />
                <span className="text-[#111827] font-semibold text-lg tracking-tight">Pulse</span>
              </div>
            </div>
          </div>

          <div className="mb-6 sm:mb-8 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-[#111827]">Welcome back</h2>
            <p className="text-sm sm:text-base text-[#6B7280] mt-1">Sign in to access your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@playershealth.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-10 sm:h-11"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-10 sm:h-11"
                data-testid="login-password-input"
              />
              <div className="flex justify-end mt-1">
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-[#2563EB] hover:text-[#1D4ED8]"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-10 sm:h-11 bg-[#2563EB] hover:bg-[#1D4ED8]" 
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
